import DragDots from "./DragDots";
import React, { useRef, DragEvent, useState } from "react";
import "./TableCheckboxList.scss";

interface Checkbox {
    title: string;
    isChecked: boolean;
    key: string;
    isDisabled?: boolean;
}

interface TableCheckboxListProps {
    checkboxes: Checkbox[];
    updateHeaders: (
        newHeaderOrder: Checkbox[],
        newHeaderDummyDataOrder: any[]
    ) => void;
    onChange: (isChecked: boolean, index: number, key: string) => void;
    dummyData: any[];
}

const TableCheckboxList: React.FC<TableCheckboxListProps> = ({
    onChange,
    dummyData,
    checkboxes,
    updateHeaders,
}) => {
    const draggedItemIndex = useRef<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const handleDragStart = (
        e: React.DragEvent<HTMLDivElement>,
        index: number
    ) => {
        const checkbox = checkboxes[index];
        // Prevent dragging of protected columns or unchecked boxes
        if (checkbox.isDisabled ||
            checkbox.key === "wellName" ||
            checkbox.title === "Well" ||
            !checkbox.isChecked) {
            e.preventDefault();
            return;
        }

        e.stopPropagation();
        draggedItemIndex.current = index;
        e.dataTransfer.effectAllowed = "move";

        // Add visual feedback
        const target = e.currentTarget;
        target.style.opacity = "0.5";
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>, overIndex: number) => {
        e.preventDefault();
        e.stopPropagation();

        // Don't allow dropping on protected columns or unchecked boxes
        const targetCheckbox = checkboxes[overIndex];
        if (targetCheckbox.isDisabled ||
            targetCheckbox.key === "wellName" ||
            targetCheckbox.title === "Well" ||
            !targetCheckbox.isChecked) {
            return;
        }

        e.dataTransfer.dropEffect = "move";
        setDragOverIndex(overIndex);
    };

    const handleDragEnter = (e: DragEvent<HTMLDivElement>, overIndex: number) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        // Only clear if we're leaving the entire component
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;

        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setDragOverIndex(null);
        }
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
        e.preventDefault();
        e.stopPropagation();

        // Don't allow dropping on protected columns or unchecked boxes
        const targetCheckbox = checkboxes[dropIndex];
        if (targetCheckbox.isDisabled ||
            targetCheckbox.key === "wellName" ||
            targetCheckbox.title === "Well" ||
            !targetCheckbox.isChecked) {
            setDragOverIndex(null);
            return;
        }

        if (draggedItemIndex.current !== null && draggedItemIndex.current !== dropIndex) {
            const newHeaderOrder = [...checkboxes];
            const newHeaderDummyDataOrder = [...dummyData];

            const draggedItem = newHeaderOrder[draggedItemIndex.current];
            const draggedItemDummy = newHeaderDummyDataOrder[draggedItemIndex.current];

            // Remove the dragged item
            newHeaderOrder.splice(draggedItemIndex.current, 1);
            newHeaderDummyDataOrder.splice(draggedItemIndex.current, 1);

            // Insert at new position
            newHeaderOrder.splice(dropIndex, 0, draggedItem);
            newHeaderDummyDataOrder.splice(dropIndex, 0, draggedItemDummy);

            updateHeaders(newHeaderOrder, newHeaderDummyDataOrder);
        }

        setDragOverIndex(null);
        draggedItemIndex.current = null;
    };

    const handleDragEnd = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        // Reset visual feedback
        const target = e.currentTarget;
        target.style.opacity = "1";
        setDragOverIndex(null);
        draggedItemIndex.current = null;
    };

    return (
        <div className="space-y-3">
            {checkboxes.map((checkbox, displayIndex) => {
                const isProtectedColumn = checkbox.isDisabled ||
                    checkbox.key === "wellName" ||
                    checkbox.title === "Well";
                
                // Determine if this checkbox can be dragged (checked and not protected)
                const canBeDragged = checkbox.isChecked && !isProtectedColumn;
                
                // Determine if this checkbox can accept drops (checked and not protected)
                const canAcceptDrop = checkbox.isChecked && !isProtectedColumn;

                return (
                    <div
                        key={`${checkbox.key}-${displayIndex}`}
                        className={`flex items-center justify-between p-2 rounded-md transition-all duration-200 ${
                            dragOverIndex === displayIndex && canAcceptDrop ? 'border-t-2 border-blue-500 pt-3' : ''
                            } ${isProtectedColumn ? 'opacity-75 bg-darkcolor-600' : 'hover:bg-darkcolor-600'}`}
                        onDragOver={canAcceptDrop ? (e) => handleDragOver(e, displayIndex) : undefined}
                        onDragEnter={canAcceptDrop ? (e) => handleDragEnter(e, displayIndex) : undefined}
                        onDragLeave={canAcceptDrop ? handleDragLeave : undefined}
                        onDrop={canAcceptDrop ? (e) => handleDrop(e, displayIndex) : undefined}
                    >
                        <div className="flex items-center flex-1">
                        <input
                            type="checkbox"
                            value=""
                            className={`custom-checkbox ${isProtectedColumn ? 'protected cursor-not-allowed' : 'cursor-pointer'}`}
                            checked={checkbox.isChecked}
                            disabled={isProtectedColumn}
                            onChange={() => {
                                if (!isProtectedColumn) {
                                    onChange(!checkbox.isChecked, displayIndex, checkbox.key);
                                }
                            }}
                        />
                            <label className={`ml-3 text-[14px] font-medium ${
                                isProtectedColumn ? 'text-gray-400' : 'text-white'
                                }`}>
                                {checkbox.title}
                            </label>
                        </div>
                        
                        {/* Only show drag dots for checked, non-protected columns */}
                        {canBeDragged ? (
                        <div
                            onDragStart={(e) => handleDragStart(e, displayIndex)}
                            onDragEnd={handleDragEnd}
                            draggable={true}
                            className="ml-2 cursor-grab active:cursor-grabbing"
                            style={{ userSelect: 'none' }}
                        >
                            <DragDots />
                        </div>
                        ) : (
                            // Empty space to maintain consistent layout
                            <div className="ml-2 w-5 h-5"></div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default TableCheckboxList;
